// Blocks Studio data model (learn-blocks-studio-prd.md §5/§7).
//
// The whole program is ONE structured JSON document — `project.blocks.json` in
// the project VFS (D-BLK-3): pages (≤4) → characters → horizontal scripts of
// icon blocks. This module owns the document shape, the v1 block catalogue
// (op → category/icon/label/param), and safe parse/serialize. The backend
// seeds the same shape (platform-backend `blocks-templates.ts`) — keep
// `version` in sync.

export const BLOCKS_PROJECT_FILE = 'project.blocks.json';
export const GRID_W = 20;
export const GRID_H = 15;
// Kids can add as many pages as they like; this is only a generous safety
// bound (a saved doc / a runaway loop can't blow up memory), never a UX cap a
// child will hit.
export const MAX_PAGES = 50;
export const MAX_PARAM = 9;
// Curriculum artwork may use a larger alpha-compensation scale than kids can
// reach with Grow. Preserve those authored starts while still bounding an
// untrusted saved document.
export const MAX_CHARACTER_START_SIZE = 6;

// ── Block catalogue (v1 set — control `repeat` C-block lands with M3) ───────
export type BlockCategory = 'trigger' | 'motion' | 'looks' | 'sound' | 'control' | 'end';

export type BlockOp =
  | 'when_flag'
  | 'when_tap'
  | 'when_bump'
  | 'when_message'
  | 'move_right'
  | 'move_left'
  | 'move_up'
  | 'move_down'
  | 'turn_right'
  | 'turn_left'
  | 'hop'
  | 'go_home'
  | 'say'
  | 'grow'
  | 'shrink'
  | 'reset_size'
  | 'hide'
  | 'show'
  | 'pop'
  | 'play_note'
  | 'play_sound'
  | 'send_message'
  | 'if_touching'
  | 'wait'
  | 'set_speed'
  | 'stop'
  | 'end'
  | 'forever'
  | 'goto_page';

/** The editable parameter on a block: a number tile, a 3-state speed, a 6-colour
 *  message tag, or none. (`hasN` stays for the number-tile blocks.) */
export type BlockParam = 'speed' | 'color' | 'note' | 'sound';

export interface BlockDef {
  op: BlockOp;
  category: BlockCategory;
  icon: string;
  label: string;
  /** Has a tap-to-edit number tile (grid squares / tenths of a second / page). */
  hasN?: boolean;
  defaultN?: number;
  /** A non-number tap-to-cycle parameter (speed level 1–3, or message colour 1–6). */
  param?: BlockParam;
  /** Accepted for old projects but omitted from the new-project palette. */
  legacy?: boolean;
}

export const MAX_SPEED = 3; // 1 slow · 2 normal · 3 fast
export const MAX_COLOR = 6; // ScratchJr-style six message colours
export const MAX_NOTE = 7; // C-major Do · Re · Mi · Fa · Sol · La · Ti
export const MAX_SOUND = 6;
/** Six message colours (index = block.n − 1). Used by Send / Get message blocks. */
export const MESSAGE_COLORS = [
  '#ff5677',
  '#ff8a2b',
  '#ffb400',
  '#1fc983',
  '#3d9bf5',
  '#a964f7',
] as const;
/** Speed level → glyph + duration multiplier (slow runs 2×, fast runs 0.5×). */
export const SPEED_ICONS = ['🐢', '🚶', '🐇'] as const;
export const SPEED_FACTORS = [2, 1, 0.5] as const;
export const BUILT_IN_NOTES = [
  { id: 1, icon: '1', label: 'Do' },
  { id: 2, icon: '2', label: 'Re' },
  { id: 3, icon: '3', label: 'Mi' },
  { id: 4, icon: '4', label: 'Fa' },
  { id: 5, icon: '5', label: 'Sol' },
  { id: 6, icon: '6', label: 'La' },
  { id: 7, icon: '7', label: 'Ti' },
] as const;
export const BUILT_IN_SOUNDS = [
  { id: 1, icon: '🫧', label: 'Bubble Pop' },
  { id: 2, icon: '🔔', label: 'Chime' },
  { id: 3, icon: '🥁', label: 'Drum' },
  { id: 4, icon: '💨', label: 'Whoosh' },
  { id: 5, icon: '🦘', label: 'Boing' },
  { id: 6, icon: '✨', label: 'Sparkle' },
] as const;

export const BLOCK_DEFS: readonly BlockDef[] = [
  { op: 'when_flag', category: 'trigger', icon: '🚩', label: 'Start' },
  { op: 'when_tap', category: 'trigger', icon: '👆', label: 'On tap' },
  { op: 'when_bump', category: 'trigger', icon: '💥', label: 'On bump' },
  { op: 'when_message', category: 'trigger', icon: '📥', label: 'Get', param: 'color' },
  { op: 'move_right', category: 'motion', icon: '➡️', label: 'Right', hasN: true, defaultN: 2 },
  { op: 'move_left', category: 'motion', icon: '⬅️', label: 'Left', hasN: true, defaultN: 2 },
  { op: 'move_up', category: 'motion', icon: '⬆️', label: 'Up', hasN: true, defaultN: 2 },
  { op: 'move_down', category: 'motion', icon: '⬇️', label: 'Down', hasN: true, defaultN: 2 },
  { op: 'turn_right', category: 'motion', icon: '↪️', label: 'Turn', hasN: true, defaultN: 3 },
  { op: 'turn_left', category: 'motion', icon: '↩️', label: 'Turn', hasN: true, defaultN: 3 },
  { op: 'hop', category: 'motion', icon: '🦘', label: 'Hop', hasN: true, defaultN: 2 },
  { op: 'go_home', category: 'motion', icon: '🏠', label: 'Home' },
  { op: 'say', category: 'looks', icon: '💬', label: 'Say' },
  { op: 'grow', category: 'looks', icon: '🔼', label: 'Grow', hasN: true, defaultN: 2 },
  { op: 'shrink', category: 'looks', icon: '🔽', label: 'Shrink', hasN: true, defaultN: 2 },
  { op: 'reset_size', category: 'looks', icon: '🔄', label: 'Reset' },
  { op: 'hide', category: 'looks', icon: '🫥', label: 'Hide' },
  { op: 'show', category: 'looks', icon: '👁', label: 'Show' },
  { op: 'pop', category: 'sound', icon: '🫧', label: 'Pop', legacy: true },
  { op: 'play_note', category: 'sound', icon: '🎵', label: 'Do', param: 'note' },
  { op: 'play_sound', category: 'sound', icon: '🫧', label: 'Pop', param: 'sound' },
  { op: 'send_message', category: 'control', icon: '📤', label: 'Send', param: 'color' },
  { op: 'if_touching', category: 'control', icon: '🤝', label: 'If touching' },
  { op: 'wait', category: 'control', icon: '⏱', label: 'Wait', hasN: true, defaultN: 5 },
  { op: 'set_speed', category: 'control', icon: '🐇', label: 'Speed', param: 'speed' },
  { op: 'stop', category: 'control', icon: '🛑', label: 'Stop' },
  { op: 'end', category: 'end', icon: '🏁', label: 'End' },
  { op: 'forever', category: 'end', icon: '♾️', label: 'Again' },
  { op: 'goto_page', category: 'end', icon: '📄', label: 'Page', hasN: true, defaultN: 1 },
] as const;

/** The starting `n` for a freshly-added block (number tile / speed / colour). */
export function defaultParam(op: BlockOp): number | undefined {
  const def = blockDef(op);
  if (def.hasN) return def.defaultN ?? 1;
  if (def.param === 'speed') return 2; // normal
  if (def.param === 'color') return 1; // first colour
  if (def.param === 'note') return 1; // Do
  if (def.param === 'sound') return 1; // Pop
  return undefined;
}

const DEFS_BY_OP = new Map(BLOCK_DEFS.map((d) => [d.op, d]));
export function blockDef(op: BlockOp): BlockDef {
  return DEFS_BY_OP.get(op)!;
}
export function isTrigger(op: BlockOp): boolean {
  return blockDef(op).category === 'trigger';
}
/**
 * C-shaped blocks: they own a nested `body` chain instead of being a single
 * flat step. Every drag/drop path in the editor branches on this, so adding a
 * new C-block (Repeat, …) is a one-line change here plus its interpreter case.
 */
export function isContainer(op: BlockOp): boolean {
  return op === 'if_touching';
}
export const CATEGORIES: readonly { id: BlockCategory; icon: string; label: string }[] = [
  { id: 'trigger', icon: '🚩', label: 'Start' },
  { id: 'motion', icon: '➡️', label: 'Move' },
  { id: 'looks', icon: '💬', label: 'Looks' },
  { id: 'sound', icon: '🔊', label: 'Sound' },
  { id: 'control', icon: '⏱', label: 'Control' },
  { id: 'end', icon: '🏁', label: 'End' },
];

// ── Document shape ───────────────────────────────────────────────────────────
export interface Block {
  op: BlockOp;
  n?: number;
  text?: string;
  /** Nested actions owned by a C-shaped control block. */
  body?: Block[];
}

/**
 * Where a block sits in a script's tree: the top-level index, then one body
 * index per C-block it is nested inside. `[3]` is the 4th block of the script;
 * `[3, 1]` is the 2nd block inside the If at `[3]`. The editor addresses every
 * drag source and drop slot this way, so nesting depth costs the drag code
 * nothing.
 */
export type BlockPath = readonly number[];

/** Resolve a path against a script's blocks (undefined if it doesn't exist). */
export function blockAtPath(blocks: Block[], path: BlockPath): Block | undefined {
  let list: Block[] | undefined = blocks;
  let found: Block | undefined;
  for (const index of path) {
    found = list?.[index];
    if (!found) return undefined;
    list = found.body;
  }
  return found;
}

export function samePath(a: BlockPath, b: BlockPath): boolean {
  return a.length === b.length && a.every((value, i) => value === b[i]);
}

/**
 * True when `inner` addresses the block at `outer` or something nested inside
 * it — the guard that stops a child dropping a C-block into its own body.
 */
export function pathIsWithin(outer: BlockPath, inner: BlockPath): boolean {
  return inner.length >= outer.length && outer.every((value, i) => value === inner[i]);
}

/** Serialise a path for a DOM `data-` attribute (`[3,1]` → `"3.1"`). */
export function pathToKey(path: BlockPath): string {
  return path.join('.');
}

/** Parse a `data-` attribute back into a path (`""` → `[]`). */
export function pathFromKey(key: string): number[] {
  return key === '' ? [] : key.split('.').map((part) => Number.parseInt(part, 10));
}

/**
 * v0.12 stored Junior If as a one-action guard. Upgrade those flat projects to
 * the paired structural form without changing what they do:
 *   If touching → action
 * becomes
 *   If touching → action → End if.
 */
type LegacyBlock = Omit<Block, 'op' | 'body'> & {
  op: BlockOp | 'end_if';
  body?: LegacyBlock[];
};

function migrateJuniorIf(blocks: LegacyBlock[]): Block[] {
  const pairedFormat = blocks.some((block) => block.op === 'end_if');
  const migrated: Block[] = [];
  for (let i = 0; i < blocks.length; i += 1) {
    const block = blocks[i];
    if (block.op === 'end_if') continue;
    if (block.op !== 'if_touching') {
      migrated.push(block as Block);
      continue;
    }
    if (Array.isArray(block.body)) {
      migrated.push({ ...(block as Block), body: migrateJuniorIf(block.body) });
      continue;
    }
    if (!pairedFormat) {
      const guarded = blocks[i + 1];
      migrated.push({
        op: 'if_touching',
        ...(block.text ? { text: block.text } : {}),
        body: guarded ? [guarded as Block] : [],
      });
      if (guarded) i += 1;
      continue;
    }
    const body: Block[] = [];
    let depth = 1;
    while (i + 1 < blocks.length) {
      const next = blocks[i + 1];
      if (next.op === 'if_touching') depth += 1;
      if (next.op === 'end_if') depth -= 1;
      i += 1;
      if (depth === 0) break;
      body.push(next as Block);
    }
    migrated.push({ op: 'if_touching', ...(block.text ? { text: block.text } : {}), body });
  }
  return migrated;
}
export interface Script {
  id: string;
  blocks: Block[];
}
export interface CharacterStart {
  gx: number;
  gy: number;
  size: number;
  rot: number;
  /**
   * Whether the character is on stage when the run begins. Defaults to true, so
   * every project authored before this field keeps its behaviour. A character
   * that starts hidden is a stage actor the story reveals with Show — not a
   * character the child removed mid-run.
   */
  visible?: boolean;
  /**
   * Optional collision reach in grid cells, decoupling the foot zone from the
   * drawn size. A wide backdrop-like actor (a water curtain) can fill the stage
   * visually while still only registering contact on the cell the route ends on.
   * Defaults to the size-derived radius.
   */
  reach?: number;
}
export interface Character {
  id: string;
  name: string;
  emoji: string;
  /** Optional first-party visual. Emoji remains the portable fallback. */
  asset?: string;
  start: CharacterStart;
  scripts: Script[];
}

const STORY_BLOCKS_ASSET_PREFIX = '/story-blocks/';

function safeCharacterAsset(value: unknown): string | undefined {
  return typeof value === 'string' && value.startsWith(STORY_BLOCKS_ASSET_PREFIX)
    ? value.slice(0, 240)
    : undefined;
}

const TINY_STAR_A1_H_ASSET =
  '/story-blocks/tiny-star-village/characters/little-light/resting-calm-v01.png';

function legacyLessonId(pages: Page[]): string | undefined {
  const page = pages[0];
  const littleLight = page?.characters.find(
    (character) => character.asset === TINY_STAR_A1_H_ASSET,
  );
  const ops = littleLight?.scripts[0]?.blocks.map((block) => block.op).join(',');
  return page?.background === 'tsv-window-room-dim' && ops === 'when_flag,say,hop,end'
    ? 'tsv-s1-a1-h'
    : undefined;
}
export interface Page {
  id: string;
  background: string;
  characters: Character[];
}
export interface BlocksExperimentVariant {
  id: string;
  label: string;
  pages: Page[];
}
export interface BlocksExperimentRunEvidence {
  variantId: string;
  endpointGx: number;
  successSignal:
    | 'door-light-hidden'
    | 'door-light-visible'
    | 'door-and-source-visible'
    | 'route-a-gx8'
    | 'route-b-gx9'
    | 'stamp-a-upper'
    | 'stamp-b-lower'
    | 'puzzle-a-observed'
    | 'puzzle-b-observed'
    | 'c5-v1-failed'
    | 'c5-v2-improved'
    | 'c5-v3-passed'
    | 'c5-v3-fault-observed'
    | 'c6-a-looped'
    | 'c6-b-reached-p2'
    | 'c6-b-reached-end';
  /** Fix Parts retain the observable run before and after one bounded repair. */
  phase?:
    | 'unfair'
    | 'repaired'
    | 'draft'
    | 'reopened'
    | 'retell'
    | 'v1'
    | 'v2'
    | 'v3'
    | 'fault'
    | 'regression'
    | 'peer';
  startGx?: number;
  collisionAtGx?: number;
  visibleActorIds?: string[];
  feedbackText?: string;
  resetToHome: boolean;
  completedAt: string;
}
export interface BlocksExperiment {
  kind: 'isolated-variants-v1';
  activeVariantId: string;
  sharedStartGx: number;
  sharedTargetGx: number;
  /** A visual comparison line may sit between integer sprite cells. */
  referenceLineGx?: number;
  /** Optional route-specific homes for experiments whose point is a changed starting route. */
  variantStartGx?: Record<string, number>;
  predictionFollowsActiveVariant?: boolean;
  /** One curriculum-owned metadata repair; scripts and target stay immutable. */
  startRepair?: {
    variantId: string;
    fromGx: number;
    toGx: number;
  };
  personalShip?: {
    targetId: 'high-door-light' | 'narrow-road-sign' | 'sleeping-bell';
    targetActorId: string;
    feedbackActorId: string;
    sharedActorIds: string[];
    initialFeedback: string;
    revisedFeedback: string;
    peerReview?: {
      chosenVariantId: 'tool-b';
      reason: 'visible-target-change';
      revisionKind: 'say';
    };
  };
  lockedPrediction: {
    partId: string;
    choice: string;
    successSignal: string;
  };
  variants: BlocksExperimentVariant[];
  runLedger: BlocksExperimentRunEvidence[];
}
export interface BlocksProject {
  version: 1;
  name: string;
  /** Optional curriculum identity used to load a first-party story mission. */
  lessonId?: string;
  pages: Page[];
  /** Bounded curriculum A/B snapshots. Only one variant is loaded and runnable at a time. */
  experiment?: BlocksExperiment;
}

let seq = 0;
export function newId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${(seq += 1)}`;
}

export function blankProject(name = 'My blocks project'): BlocksProject {
  return {
    version: 1,
    name,
    pages: [
      {
        id: newId('page'),
        background: 'meadow',
        characters: [
          {
            id: newId('char'),
            name: 'Cat',
            emoji: '🐱',
            start: { gx: 5, gy: 10, size: 1, rot: 0 },
            scripts: [],
          },
        ],
      },
    ],
  };
}

// ── Safe parse / serialize ───────────────────────────────────────────────────
const clampN = (v: unknown, lo: number, hi: number, dflt: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, Math.round(v))) : dflt;

/**
 * Parse a project document defensively: unknown ops are dropped, params are
 * clamped, page/character invariants enforced. Anything unrecoverable falls
 * back to a blank project rather than trapping the kid on a broken file.
 */
export function parseProject(raw: string): BlocksProject {
  try {
    const doc = JSON.parse(raw) as Partial<BlocksProject>;
    if (!doc || doc.version !== 1 || !Array.isArray(doc.pages) || doc.pages.length === 0) {
      return blankProject();
    }
    const pages: Page[] = doc.pages.slice(0, MAX_PAGES).map((p, pi) => ({
      id: typeof p?.id === 'string' && p.id ? p.id : newId('page'),
      background: typeof p?.background === 'string' ? p.background : 'meadow',
      characters: (Array.isArray(p?.characters) ? p.characters : []).map((c, ci) => {
        const asset = safeCharacterAsset(c?.asset);
        return {
          id: typeof c?.id === 'string' && c.id ? c.id : newId('char'),
          name: typeof c?.name === 'string' && c.name ? c.name.slice(0, 24) : `Friend ${ci + 1}`,
          emoji: typeof c?.emoji === 'string' && c.emoji ? c.emoji : '🐱',
          ...(asset ? { asset } : {}),
          start: {
            gx: clampN(c?.start?.gx, 0, GRID_W - 1, 5),
            gy: clampN(c?.start?.gy, 0, GRID_H - 1, 10),
            size:
              typeof c?.start?.size === 'number'
                ? Math.min(MAX_CHARACTER_START_SIZE, Math.max(0.3, c.start.size))
                : 1,
            rot: clampN(c?.start?.rot, -360, 360, 0),
            ...(c?.start?.visible === false ? { visible: false } : {}),
            ...(typeof c?.start?.reach === 'number'
              ? { reach: clampN(c.start.reach, 0.1, 3, 0.5) }
              : {}),
          },
          scripts: (Array.isArray(c?.scripts) ? c.scripts : [])
            .map((s) => ({
              id: typeof s?.id === 'string' && s.id ? s.id : newId('script'),
              blocks: migrateJuniorIf(
                ((Array.isArray(s?.blocks) ? s.blocks : []) as unknown[])
                  .filter((b): b is LegacyBlock => {
                    if (!b || typeof b !== 'object' || !('op' in b)) return false;
                    const op = (b as { op?: unknown }).op;
                    return (
                      op === 'end_if' || (typeof op === 'string' && DEFS_BY_OP.has(op as BlockOp))
                    );
                  })
                  .map((b) => {
                    if (b.op === 'end_if') return { op: 'end_if' as const };
                    const def = blockDef(b.op);
                    const out: Block = { op: b.op };
                    if (def.hasN) out.n = clampN(b.n, 1, MAX_PARAM, def.defaultN ?? 1);
                    if (b.n !== undefined && def.param === 'speed')
                      out.n = clampN(b.n, 1, MAX_SPEED, 2);
                    if (b.n !== undefined && def.param === 'color')
                      out.n = clampN(b.n, 1, MAX_COLOR, 1);
                    if (b.n !== undefined && def.param === 'note')
                      out.n = clampN(b.n, 1, MAX_NOTE, 1);
                    if (b.n !== undefined && def.param === 'sound')
                      out.n = clampN(b.n, 1, MAX_SOUND, 1);
                    if (b.op === 'say')
                      out.text = (typeof b.text === 'string' ? b.text : 'Hi!').slice(0, 60);
                    if (b.op === 'if_touching' && typeof b.text === 'string') {
                      out.text = b.text.slice(0, 80);
                    }
                    if (b.op === 'if_touching' && Array.isArray(b.body)) {
                      out.body = migrateJuniorIf(b.body);
                    }
                    return out;
                  }),
              ),
            }))
            .filter((s) => s.blocks.length > 0 && isTrigger(s.blocks[0].op)),
        };
      }),
      // every page must have at least one character to code
      ...(pi === 0 ? {} : {}),
    }));
    for (const page of pages) {
      if (page.characters.length === 0) {
        page.characters.push(blankProject().pages[0].characters[0]);
      }
    }
    const lessonId =
      typeof doc.lessonId === 'string' && /^[a-z0-9-]{1,64}$/.test(doc.lessonId)
        ? doc.lessonId
        : legacyLessonId(pages);
    const experiment = (() => {
      const candidate = doc.experiment;
      if (candidate?.kind !== 'isolated-variants-v1' || !Array.isArray(candidate.variants)) {
        return undefined;
      }
      const variants = candidate.variants
        .slice(0, 6)
        .filter((variant): variant is BlocksExperimentVariant =>
          Boolean(
            variant &&
            typeof variant.id === 'string' &&
            /^[a-z0-9-]{1,32}$/u.test(variant.id) &&
            typeof variant.label === 'string' &&
            Array.isArray(variant.pages) &&
            variant.pages.length > 0,
          ),
        )
        .map((variant) => ({
          id: variant.id,
          label: variant.label.slice(0, 40),
          pages: parseProject(
            JSON.stringify({ version: 1, name: 'Experiment variant', pages: variant.pages }),
          ).pages,
        }));
      const singlePersonalPeerTest =
        lessonId === 'jtw-s4-c6-p7' &&
        variants.length === 1 &&
        variants[0]?.id === 'personal';
      if (
        (!singlePersonalPeerTest && variants.length < 2) ||
        new Set(variants.map((variant) => variant.id)).size !== variants.length
      ) {
        return undefined;
      }
      const activeVariantId = variants.some((variant) => variant.id === candidate.activeVariantId)
        ? candidate.activeVariantId
        : variants[0].id;
      const lockedPrediction = candidate.lockedPrediction;
      if (
        !lockedPrediction ||
        typeof lockedPrediction.partId !== 'string' ||
        typeof lockedPrediction.choice !== 'string' ||
        typeof lockedPrediction.successSignal !== 'string'
      ) {
        return undefined;
      }
      const variantIds = new Set(variants.map((variant) => variant.id));
      const ledgerPhaseCount = new Set(
        (Array.isArray(candidate.runLedger) ? candidate.runLedger : []).map((entry) =>
          entry && typeof entry === 'object' && typeof entry.phase === 'string'
            ? entry.phase
            : 'none',
        ),
      ).size;
      const runLedger = (Array.isArray(candidate.runLedger) ? candidate.runLedger : [])
        .filter((entry): entry is BlocksExperimentRunEvidence =>
          Boolean(
            entry &&
            typeof entry.variantId === 'string' &&
            variantIds.has(entry.variantId) &&
            typeof entry.endpointGx === 'number' &&
            (entry.successSignal === 'door-light-hidden' ||
              entry.successSignal === 'door-light-visible' ||
              entry.successSignal === 'door-and-source-visible' ||
              entry.successSignal === 'route-a-gx8' ||
              entry.successSignal === 'route-b-gx9' ||
              entry.successSignal === 'stamp-a-upper' ||
              entry.successSignal === 'stamp-b-lower' ||
              entry.successSignal === 'puzzle-a-observed' ||
              entry.successSignal === 'puzzle-b-observed' ||
              entry.successSignal === 'c5-v1-failed' ||
              entry.successSignal === 'c5-v2-improved' ||
              entry.successSignal === 'c5-v3-passed' ||
              entry.successSignal === 'c5-v3-fault-observed' ||
              entry.successSignal === 'c6-a-looped' ||
              entry.successSignal === 'c6-b-reached-p2' ||
              entry.successSignal === 'c6-b-reached-end') &&
            entry.resetToHome === true &&
            typeof entry.completedAt === 'string',
          ),
        )
        .slice(
          -(
            variants.length *
            Math.max(
              1,
              candidate.startRepair ? 2 : 1,
              candidate.personalShip ? 3 : 1,
              Math.min(6, ledgerPhaseCount),
            )
          ),
        )
        .map((entry) => ({
          ...entry,
          ...(entry.phase === 'unfair' ||
          entry.phase === 'repaired' ||
          entry.phase === 'draft' ||
          entry.phase === 'reopened' ||
          entry.phase === 'retell' ||
          entry.phase === 'v1' ||
          entry.phase === 'v2' ||
          entry.phase === 'v3' ||
          entry.phase === 'fault' ||
          entry.phase === 'regression' ||
          entry.phase === 'peer'
            ? { phase: entry.phase }
            : {}),
          ...(typeof entry.startGx === 'number'
            ? { startGx: clampN(entry.startGx, 0, GRID_W - 1, 4) }
            : {}),
          ...(typeof entry.collisionAtGx === 'number'
            ? { collisionAtGx: clampN(entry.collisionAtGx, 0, GRID_W - 1, 10) }
            : {}),
          ...(Array.isArray(entry.visibleActorIds)
            ? {
                visibleActorIds: entry.visibleActorIds
                  .filter((id): id is string => typeof id === 'string')
                  .slice(0, 8)
                  .map((id) => id.slice(0, 64)),
              }
            : {}),
          ...(typeof entry.feedbackText === 'string'
            ? { feedbackText: entry.feedbackText.slice(0, 60) }
            : {}),
        }));
      return {
        kind: 'isolated-variants-v1' as const,
        activeVariantId,
        sharedStartGx: clampN(candidate.sharedStartGx, 0, GRID_W - 1, 4),
        sharedTargetGx: clampN(candidate.sharedTargetGx, 0, GRID_W - 1, 10),
        ...(typeof candidate.referenceLineGx === 'number' &&
        Number.isFinite(candidate.referenceLineGx)
          ? { referenceLineGx: Math.min(GRID_W - 1, Math.max(0, candidate.referenceLineGx)) }
          : {}),
        ...(candidate.variantStartGx && typeof candidate.variantStartGx === 'object'
          ? {
              variantStartGx: Object.fromEntries(
                variants.map((variant) => [
                  variant.id,
                  clampN(candidate.variantStartGx?.[variant.id], 0, GRID_W - 1, 4),
                ]),
              ),
            }
          : {}),
        ...(candidate.predictionFollowsActiveVariant === true
          ? { predictionFollowsActiveVariant: true }
          : {}),
        ...(candidate.startRepair &&
        typeof candidate.startRepair === 'object' &&
        typeof candidate.startRepair.variantId === 'string' &&
        variantIds.has(candidate.startRepair.variantId)
          ? {
              startRepair: {
                variantId: candidate.startRepair.variantId,
                fromGx: clampN(candidate.startRepair.fromGx, 0, GRID_W - 1, 8),
                toGx: clampN(candidate.startRepair.toGx, 0, GRID_W - 1, 4),
              },
            }
          : {}),
        ...(candidate.personalShip &&
        typeof candidate.personalShip === 'object' &&
        (candidate.personalShip.targetId === 'high-door-light' ||
          candidate.personalShip.targetId === 'narrow-road-sign' ||
          candidate.personalShip.targetId === 'sleeping-bell') &&
        typeof candidate.personalShip.targetActorId === 'string' &&
        typeof candidate.personalShip.feedbackActorId === 'string' &&
        Array.isArray(candidate.personalShip.sharedActorIds) &&
        typeof candidate.personalShip.initialFeedback === 'string' &&
        typeof candidate.personalShip.revisedFeedback === 'string'
          ? {
              personalShip: {
                targetId: candidate.personalShip.targetId,
                targetActorId: candidate.personalShip.targetActorId.slice(0, 64),
                feedbackActorId: candidate.personalShip.feedbackActorId.slice(0, 64),
                sharedActorIds: candidate.personalShip.sharedActorIds
                  .filter((id): id is string => typeof id === 'string')
                  .slice(0, 4)
                  .map((id) => id.slice(0, 64)),
                initialFeedback: candidate.personalShip.initialFeedback.slice(0, 60),
                revisedFeedback: candidate.personalShip.revisedFeedback.slice(0, 60),
                ...(candidate.personalShip.peerReview?.chosenVariantId === 'tool-b' &&
                candidate.personalShip.peerReview.reason === 'visible-target-change' &&
                candidate.personalShip.peerReview.revisionKind === 'say'
                  ? { peerReview: candidate.personalShip.peerReview }
                  : {}),
              },
            }
          : {}),
        lockedPrediction: {
          partId: lockedPrediction.partId.slice(0, 64),
          choice: lockedPrediction.choice.slice(0, 64),
          successSignal: lockedPrediction.successSignal.slice(0, 64),
        },
        variants,
        runLedger,
      };
    })();
    const activePages = experiment?.variants.find(
      (variant) => variant.id === experiment.activeVariantId,
    )?.pages;
    return {
      version: 1,
      name: typeof doc.name === 'string' ? doc.name.slice(0, 120) : 'My blocks project',
      ...(lessonId ? { lessonId } : {}),
      pages: activePages ?? pages,
      ...(experiment ? { experiment } : {}),
    };
  } catch {
    return blankProject();
  }
}

export function serializeProject(project: BlocksProject): string {
  return JSON.stringify(project, null, 2);
}
